import { useMemo, useState } from "react";
import { ChevronRight, Download, Eye, Filter, Loader2 } from "lucide-react";

import {
  BatchAttachmentPreviewDialog,
  type BatchAttachmentRef,
} from "@/components/common/BatchAttachmentPreviewDialog";
import { isInlinePreviewableFilename } from "@/lib/kaiAttachments";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import { Badge } from "@/components/ui/badge";
import { BookingReportColumnEditor } from "@/features/bookings/report/BookingReportColumnEditor";
import {
  BOOKING_REPORT_FILTER_LABELS,
  resolveBookingReportColumns,
} from "@/features/bookings/report/bookingLabels";
import { reportConfigFilterEntries } from "@/features/bookings/report/bookingReportConfig";
import type { FileRef, ReportDownloadRef } from "./kaiToolDownloads";

function ChipButton({
  label,
  downloading,
  onClick,
  icon: Icon = Download,
}: {
  label: string;
  downloading: boolean;
  onClick: () => void;
  icon?: typeof Download;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={downloading}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-foreground/[0.03] px-3 py-1 text-xs text-foreground/90 transition-colors hover:bg-accent/40",
        downloading && "opacity-60",
      )}
    >
      {downloading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Icon className="size-3.5" />
      )}
      <span className="max-w-[16rem] truncate">{label}</span>
    </button>
  );
}

function DownloadChip({
  fileRef,
  onPreview,
}: {
  fileRef: FileRef;
  onPreview?: (ref: BatchAttachmentRef) => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const filename = fileRef.filename ?? fileRef.label;
  const previewable = isInlinePreviewableFilename(filename);

  const handleClick = async () => {
    setDownloading(true);
    try {
      const { data, filename } = await api.getAttachmentData(
        fileRef.batchId,
        fileRef.attachmentId,
        fileRef.filename,
      );
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("Failed to download file:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {previewable && onPreview && (
        <ChipButton
          label="Preview"
          downloading={false}
          icon={Eye}
          onClick={() =>
            onPreview({
              batchId: fileRef.batchId,
              attachmentId: fileRef.attachmentId,
              filename,
            })
          }
        />
      )}
      <ChipButton label={fileRef.label} downloading={downloading} onClick={handleClick} />
    </>
  );
}

function ReportDownloadChip({ reportRef }: { reportRef: ReportDownloadRef }) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    setDownloading(true);
    try {
      await api.downloadReportPreview(
        reportRef.reportType,
        reportRef.date,
        reportRef.config,
      );
    } catch (err) {
      console.warn("Failed to download report:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ChipButton label={reportRef.label} downloading={downloading} onClick={handleClick} />
  );
}

/**
 * Interactive booking-report widget: shows the prepared export and lets the
 * user reorder/add/remove columns before downloading — no need to ask Kai to
 * regenerate for column tweaks.
 */
function KaiBookingReportCard({ reportRef }: { reportRef: ReportDownloadRef }) {
  const { label, date, config, reportType } = reportRef;
  const initialColumns = useMemo(
    () => resolveBookingReportColumns(config),
    [config],
  );
  const appliedFilters = useMemo(
    () => reportConfigFilterEntries(config, BOOKING_REPORT_FILTER_LABELS),
    [config],
  );
  const [columns, setColumns] = useState(initialColumns);
  const [downloading, setDownloading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const downloadConfig = useMemo(() => {
    const next = { ...(config ?? {}) };
    if (columns.length > 0) next.columns = columns;
    else delete next.columns;
    return next;
  }, [config, columns]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await api.downloadReportPreview(reportType, date, downloadConfig);
    } catch (err) {
      console.warn("Failed to download report:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="glass-panel my-1 flex flex-col gap-3 rounded-xl p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground">
          <Download className="size-4 shrink-0" />
          <span className="truncate">{label}</span>
        </span>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90",
            downloading && "opacity-60",
          )}
        >
          {downloading && <Loader2 className="size-3.5 animate-spin" />}
          Download
        </button>
      </div>

      {appliedFilters.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
            <Filter className="size-3.5 shrink-0 text-muted-foreground" />
            Filters Kai applied
          </p>
          <div className="flex flex-wrap gap-1.5">
            {appliedFilters.map((item) => (
              <Badge
                key={item.key}
                variant="secondary"
                className="max-w-full text-[11px] font-normal"
              >
                <span className="truncate">
                  {item.label}: {item.value}
                </span>
              </Badge>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Read-only audit of this file. Ask Kai in chat to change the filters.
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No row filters — this export includes every booking matching the
          default columns.
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        Adjust the columns below before downloading if you like.
      </p>

      <div className="rounded-lg border border-border/50">
        <button
          type="button"
          onClick={() => setEditorOpen((v) => !v)}
          aria-expanded={editorOpen}
          className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs font-medium text-foreground/80"
        >
          <ChevronRight
            className={cn(
              "size-3.5 shrink-0 text-muted-foreground transition-transform",
              editorOpen && "rotate-90",
            )}
          />
          {columns.length} columns
        </button>
        {editorOpen && (
          <div className="border-t border-border/40 p-2">
            <BookingReportColumnEditor columns={columns} onChange={setColumns} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders the download references from a tool group: booking-report cards
 * (full editor) plus file / schedule-report chips. When more than one chip is
 * present the chip list collapses behind a toggle so a large list result
 * doesn't flood the chat.
 */
export function KaiDownloads({
  fileRefs,
  reportRefs,
}: {
  fileRefs: FileRef[];
  reportRefs: ReportDownloadRef[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [batchPreview, setBatchPreview] = useState<BatchAttachmentRef | null>(
    null,
  );

  const bookingReports = reportRefs.filter((r) => r.reportType === "booking_dump");
  const chipReports = reportRefs.filter((r) => r.reportType !== "booking_dump");
  const chipTotal = fileRefs.length + chipReports.length;

  if (chipTotal === 0 && bookingReports.length === 0) return null;

  const chips =
    chipTotal > 0 ? (
      <div className="flex flex-wrap gap-1.5">
        {fileRefs.map((ref) => (
          <DownloadChip
            key={ref.attachmentId}
            fileRef={ref}
            onPreview={setBatchPreview}
          />
        ))}
        {chipReports.map((ref) => (
          <ReportDownloadChip key={ref.key} reportRef={ref} />
        ))}
      </div>
    ) : null;

  const bookingCards =
    bookingReports.length > 0 ? (
      <div className="flex flex-col gap-2">
        {bookingReports.map((ref) => (
          <KaiBookingReportCard key={ref.key} reportRef={ref} />
        ))}
      </div>
    ) : null;

  const previewDialog = (
    <BatchAttachmentPreviewDialog
      open={batchPreview !== null}
      onOpenChange={(open) => {
        if (!open) setBatchPreview(null);
      }}
      attachment={batchPreview}
    />
  );

  if (chipTotal <= 1 && bookingReports.length === 0) {
    return (
      <>
        {chips}
        {previewDialog}
      </>
    );
  }
  if (chipTotal === 0 && bookingReports.length > 0) return bookingCards;

  return (
    <>
      <div className="flex flex-col gap-2">
        {bookingCards}
        {chipTotal > 1 ? (
          <>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="inline-flex w-fit items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronRight
                className={cn(
                  "size-3.5 shrink-0 transition-transform",
                  expanded && "rotate-90",
                )}
              />
              <Download className="size-3.5" />
              {expanded ? "Hide" : "Show"} {chipTotal} files
            </button>
            {expanded && chips}
          </>
        ) : (
          chips
        )}
      </div>
      {previewDialog}
    </>
  );
}
